import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Card, Form, Input, Button, Typography, message, Spin, Space, Upload } from 'antd';
import { ArrowRightOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useAuth } from '../contexts/AuthContext';
import ContactForm from '../components/ContactForm';
import SiteBeianRecords from '../components/SiteBeianRecords';
import { HERO_BACKGROUND_MP4 } from '../constants/publicAssets';
import '../pages/HomePage.css';
import '../components/LoginModal.css';
import './merchantRegister.css';

const { Title, Text } = Typography;

type DocUrlUploadProps = {
  value?: string;
  onChange?: (url?: string) => void;
  fileList: UploadFile[];
  setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  uploadDoc: (file: File) => Promise<string>;
  buttonText: string;
};

/** 将上传得到的 URL 交给 Form.Item（value/onChange），避免 hidden 字段校验失败却看不到提示 */
const DocUrlUpload: React.FC<DocUrlUploadProps> = ({
  onChange,
  fileList,
  setFileList,
  uploadDoc,
  buttonText
}) => (
  <Upload
    fileList={fileList}
    maxCount={1}
    accept=".pdf,image/*"
    beforeUpload={async (file) => {
      try {
        const url = await uploadDoc(file);
        onChange?.(url);
        setFileList([{ uid: file.uid, name: file.name, status: 'done' }]);
        message.success(`${buttonText}已上传`);
      } catch (e) {
        message.error(e instanceof Error ? e.message : '上传失败');
      }
      return false;
    }}
    onRemove={() => {
      onChange?.(undefined);
      setFileList([]);
    }}
  >
    <Button icon={<UploadOutlined />}>{buttonText}</Button>
  </Upload>
);

/**
 * 商家注册：邮箱验证码校验真实性；样式与成员登录页一致。
 */
const MerchantRegister: React.FC = () => {
  const { token: inviteToken } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [hint, setHint] = useState('');
  const [needMerchantName, setNeedMerchantName] = useState(true);
  const [heroVideoBroken, setHeroVideoBroken] = useState(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [codeSeconds, setCodeSeconds] = useState(0);
  const [sendCodeLoading, setSendCodeLoading] = useState(false);
  const [licenseFileList, setLicenseFileList] = useState<UploadFile[]>([]);
  const [commitmentFileList, setCommitmentFileList] = useState<UploadFile[]>([]);
  const [form] = Form.useForm();
  const videoRef = useRef<HTMLVideoElement>(null);

  const uploadDoc = async (file: File) => {
    if (!inviteToken) throw new Error('邀请无效');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('inviteToken', inviteToken);
    const res = await fetch('/api/upload/merchant-document', { method: 'POST', body: fd });
    const body = await res.json();
    if (!res.ok || !body.success || !body.url) {
      throw new Error(body.error || '上传失败');
    }
    return body.url as string;
  };

  useEffect(() => {
    if (codeSeconds <= 0) return undefined;
    const t = window.setTimeout(() => setCodeSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [codeSeconds]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          video.style.pointerEvents = 'none';
        })
        .catch(() => {
          const handleInteraction = () => {
            video.play().catch(() => {});
            document.removeEventListener('touchstart', handleInteraction);
            document.removeEventListener('click', handleInteraction);
          };
          document.addEventListener('touchstart', handleInteraction, { once: true });
          document.addEventListener('click', handleInteraction, { once: true });
        });
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!inviteToken) {
        setChecking(false);
        return;
      }
      try {
        const res = await fetch(`/api/merchant/invites/${inviteToken}`);
        const body = await res.json();
        if (!res.ok || !body.success) {
          message.error(body.error || '邀请链接无效');
          setChecking(false);
          return;
        }
        setHint(body.data.merchantNameHint || '');
        setNeedMerchantName(!body.data.existingMerchantId);
        if (body.data.merchantNameHint) {
          form.setFieldsValue({ merchantName: body.data.merchantNameHint });
        }
      } catch {
        message.error('无法校验邀请链接');
      } finally {
        setChecking(false);
      }
    };
    void load();
  }, [inviteToken, form]);

  const handleSendRegisterCode = async () => {
    try {
      const email = await form.validateFields(['email']).then((v) => v.email as string);
      setSendCodeLoading(true);
      const res = await fetch('/api/merchant/register/send-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const body = await res.json();
      if (!res.ok || !body.success) {
        message.error(body.error || '发送失败');
        return;
      }
      if (body.devBypass) {
        message.success('开发模式：请使用验证码 000000');
      } else {
        message.success(body.message || '验证码已发送，请查收邮箱');
      }
      setCodeSeconds(60);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      message.error('发送失败');
    } finally {
      setSendCodeLoading(false);
    }
  };

  const onFinishFailed = () => {
    const fields = form.getFieldsError().filter((f) => f.errors.length > 0);
    const first = fields[0]?.errors?.[0];
    message.error(first || '请完善必填项后再提交');
  };

  const onFinish = async (values: Record<string, string>) => {
    if (!inviteToken) return;
    setLoading(true);
    try {
      const res = await fetch('/api/merchant/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: inviteToken, ...values })
      });
      let body: {
        success?: boolean;
        error?: string;
        data?: {
          token?: string;
          username?: string;
          realName?: string;
          merchantId?: string;
          merchantName?: string;
          merchantCode?: string;
          reviewStatus?: string;
        };
      };
      try {
        body = await res.json();
      } catch {
        message.error('服务器响应异常，请稍后重试');
        return;
      }
      if (!res.ok || !body.success || !body.data) {
        message.error(body.error || '注册失败');
        return;
      }
      const d = body.data;
      localStorage.setItem('merchantId', d.merchantId || '');
      localStorage.setItem('merchantName', d.merchantName || '');
      localStorage.setItem('merchantCode', d.merchantCode || '');
      login(
        d.token || '',
        '商家',
        d.username || '',
        d.realName,
        undefined,
        false,
        true,
        undefined,
        undefined,
        undefined,
        d.merchantName
      );
      message.success(
        d.reviewStatus === 'approved' ? '注册成功' : '注册成功，请等待平台审核资质材料'
      );
      navigate(d.reviewStatus === 'approved' ? '/merchant/orders' : '/merchant/shop');
    } catch {
      message.error('网络异常');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="homepage-container">
      <div className="hero-section">
        <video
          ref={videoRef}
          className="video-background"
          autoPlay
          muted
          playsInline
          loop
          preload="auto"
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback"
          onError={() => setHeroVideoBroken(true)}
          style={
            {
              pointerEvents: 'none',
              display: heroVideoBroken ? 'none' : undefined
            } as React.CSSProperties
          }
          {...({
            'webkit-playsinline': 'true',
            'x5-playsinline': 'true',
            'x5-video-player-type': 'h5',
            'x5-video-player-fullscreen': 'true'
          } as Record<string, string>)}
        >
          <source src={HERO_BACKGROUND_MP4} type="video/mp4" />
          您的浏览器不支持视频播放。
        </video>

        <div className="video-overlay" />

        <nav className="homepage-nav">
          <div className="nav-links">
            <Link to="/" className="nav-link">
              首页
            </Link>
            <button type="button" className="nav-link" onClick={() => setIsContactFormOpen(true)}>
              联系我们
            </button>
          </div>
        </nav>

        <div className="homepage-main-content">
          <div className="homepage-main-inner">
            <div className="content-center homepage-hero-text">
              <h1 className="main-headline">LabMind实验室管理系统</h1>
              <p className="sub-headline">商家端 · 完成注册后即可管理订单</p>
            </div>

            <div className="home-login-card-wrap merchant-register-card-wrap">
              <Card className="home-login-card home-login-card--precision" variant="borderless">
                <div className="precision-auth-surface home-precision-auth-surface">
                  {checking ? (
                    <div style={{ textAlign: 'center', padding: 48 }}>
                      <Spin size="large" />
                    </div>
                  ) : (
                    <>
                      <div className="login-header--precision home-login-auth-header">
                        <Title level={3} className="login-headline home-labmind-title">
                          LabMind实验室管理系统
                        </Title>
                        <Text className="login-lead">
                          {hint ? `商家注册 · ${hint}` : '通过平台管理员分享的链接完成注册'}
                        </Text>
                      </div>

                      <Form
                        form={form}
                        layout="vertical"
                        size="large"
                        className="login-form login-form--precision merchant-register-form-grid"
                        colon={false}
                        scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                      >
                        {needMerchantName && (
                          <Form.Item
                            className="login-lm-field login-lm-field--full"
                            name="merchantName"
                            label={<span className="login-label-block">商家名称</span>}
                            rules={[{ required: true, message: '请填写商家名称' }]}
                          >
                            <Input placeholder="商家名称" />
                          </Form.Item>
                        )}
                        <Form.Item
                          className="login-lm-field"
                          name="name"
                          label={<span className="login-label-block">联系人姓名</span>}
                          rules={[{ required: true, message: '请填写联系人' }]}
                        >
                          <Input placeholder="联系人" />
                        </Form.Item>
                        <Form.Item
                          className="login-lm-field"
                          name="phone"
                          label={<span className="login-label-block">电话</span>}
                        >
                          <Input placeholder="联系电话（选填）" />
                        </Form.Item>
                        <Form.Item
                          className="login-lm-field"
                          name="email"
                          label={<span className="login-label-block">邮箱</span>}
                          rules={[
                            { required: true, message: '请填写邮箱' },
                            { type: 'email', message: '邮箱格式不正确' }
                          ]}
                        >
                          <Input placeholder="用于验证与接收订单通知" autoComplete="email" />
                        </Form.Item>
                        <Form.Item
                          className="login-lm-field"
                          label={<span className="login-label-block">验证码</span>}
                          required
                        >
                          <Space.Compact style={{ width: '100%' }}>
                            <Form.Item
                              name="code"
                              noStyle
                              rules={[{ required: true, message: '请输入验证码' }]}
                            >
                              <Input placeholder="6 位数字" maxLength={6} style={{ width: '100%' }} />
                            </Form.Item>
                            <Button
                              type="default"
                              className="home-register-send-code-btn"
                              loading={sendCodeLoading}
                              disabled={codeSeconds > 0}
                              onClick={() => void handleSendRegisterCode()}
                            >
                              {codeSeconds > 0 ? `${codeSeconds}s` : '获取验证码'}
                            </Button>
                          </Space.Compact>
                        </Form.Item>
                        <Form.Item
                          className="login-lm-field"
                          name="username"
                          label={<span className="login-label-block">登录用户名</span>}
                          rules={[{ required: true, message: '请设置用户名' }]}
                        >
                          <Input placeholder="登录用户名" autoComplete="username" />
                        </Form.Item>
                        <Form.Item
                          className="login-lm-field"
                          name="password"
                          label={<span className="login-label-block">密码</span>}
                          rules={[
                            { required: true, message: '请设置密码' },
                            { min: 6, message: '至少 6 位' }
                          ]}
                        >
                          <Input.Password placeholder="至少 6 位" autoComplete="new-password" />
                        </Form.Item>

                        <Form.Item
                          className="login-lm-field login-lm-field--full"
                          name="address"
                          label={<span className="login-label-block">商铺地址</span>}
                        >
                          <Input.TextArea rows={2} placeholder="经营地址（选填）" />
                        </Form.Item>
                        <Form.Item
                          className="login-lm-field"
                          name="principalName"
                          label={<span className="login-label-block">负责人姓名</span>}
                          rules={[{ required: true, message: '请填写身份证姓名' }]}
                          extra="须与身份证一致，并与承诺书填写一致"
                        >
                          <Input placeholder="身份证姓名" />
                        </Form.Item>
                        <Form.Item
                          className="login-lm-field"
                          name="principalIdNumber"
                          label={<span className="login-label-block">负责人身份证号</span>}
                          rules={[
                            { required: true, message: '请填写身份证号' },
                            { pattern: /^\d{17}[\dXx]$/, message: '请输入18位身份证号' }
                          ]}
                        >
                          <Input placeholder="18 位身份证号" />
                        </Form.Item>
                        <Form.Item
                          className="login-lm-field login-lm-field--full"
                          name="businessLicenseUrl"
                          label={<span className="login-label-block">营业执照 *</span>}
                          rules={[{ required: true, message: '请上传营业执照' }]}
                          valuePropName="fileUrl"
                          getValueFromEvent={() => form.getFieldValue('businessLicenseUrl')}
                        >
                          <Upload
                            fileList={licenseFileList}
                            maxCount={1}
                            accept=".pdf,image/*"
                            beforeUpload={async (file) => {
                              try {
                                const url = await uploadDoc(file);
                                form.setFieldsValue({ businessLicenseUrl: url });
                                setLicenseFileList([
                                  { uid: file.uid, name: file.name, status: 'done' }
                                ]);
                                message.success('营业执照已上传');
                              } catch (e) {
                                message.error(e instanceof Error ? e.message : '上传失败');
                              }
                              return false;
                            }}
                            onRemove={() => {
                              form.setFieldsValue({ businessLicenseUrl: undefined });
                              setLicenseFileList([]);
                            }}
                          >
                            <Button icon={<UploadOutlined />}>上传营业执照（PDF/图片）</Button>
                          </Upload>
                        </Form.Item>
                        <Form.Item
                          className="login-lm-field login-lm-field--full"
                          name="commitmentLetterUrl"
                          label={
                            <span className="login-label-block">
                              承诺书扫描件 *{' '}
                              <a
                                href={`/merchant/commitment-template${
                                  form.getFieldValue('merchantName')
                                    ? `?name=${encodeURIComponent(form.getFieldValue('merchantName'))}`
                                    : hint
                                      ? `?name=${encodeURIComponent(hint)}`
                                      : ''
                                }`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                下载模板
                              </a>
                            </span>
                          }
                          rules={[{ required: true, message: '请上传承诺书' }]}
                          extra="请打印模板，手写签字或加盖公章后扫描/拍照上传"
                          valuePropName="fileUrl"
                          getValueFromEvent={() => form.getFieldValue('commitmentLetterUrl')}
                        >
                          <Upload
                            fileList={commitmentFileList}
                            maxCount={1}
                            accept=".pdf,image/*"
                            beforeUpload={async (file) => {
                              try {
                                const url = await uploadDoc(file);
                                form.setFieldsValue({ commitmentLetterUrl: url });
                                setCommitmentFileList([
                                  { uid: file.uid, name: file.name, status: 'done' }
                                ]);
                                message.success('承诺书已上传');
                              } catch (e) {
                                message.error(e instanceof Error ? e.message : '上传失败');
                              }
                              return false;
                            }}
                            onRemove={() => {
                              form.setFieldsValue({ commitmentLetterUrl: undefined });
                              setCommitmentFileList([]);
                            }}
                          >
                            <Button icon={<UploadOutlined />}>上传签字/盖章承诺书</Button>
                          </Upload>
                        </Form.Item>

                        <Form.Item className="login-button-container login-lm-field--full">
                          <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            block
                            size="large"
                            className="login-modal__submit"
                            icon={loading ? undefined : <ArrowRightOutlined />}
                            iconPosition="end"
                          >
                            {loading ? '提交中...' : '完成注册'}
                          </Button>
                        </Form.Item>
                      </Form>

                      <div className="home-auth-card-footer">
                        <Button
                          type="default"
                          block
                          size="large"
                          className="home-wechat-login-btn"
                          onClick={() => navigate('/merchant/login')}
                        >
                          已有账号？去登录
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <ContactForm isOpen={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} />

      <footer className="homepage-site-footer" role="contentinfo">
        <SiteBeianRecords />
      </footer>
    </div>
  );
};

export default MerchantRegister;
